import React from 'react'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import {
  IAdminSellerActivity,
  IAdminSellerActivityOrders,
  IAdminSellerCabinetVisits,
  IAdminSellerResponseTime
} from 'models/seller'
import {
  ISegmentBarItem,
  SegmentBar,
  SegmentDot
} from 'components/sellers/SegmentBar/SegmentBar'
import { SellerDisableStatus } from 'components/sellers/SellerDisableStatus/SellerDisableStatus'
import { StockTermsHint } from 'components/sellers/StockConfirmationStatus/ui/StockTermsHint'
import {
  formatStockConfirmationDate,
  getStockConfirmationSourceText,
  getStockConfirmationTag,
  getStockDaysText
} from 'components/sellers/StockConfirmationStatus/utils/stockConfirmation'
import { formatMinutes } from 'shared/utils/formatMinutes'
import { getDeclination } from 'shared/utils/getDeclination'
import styles from './SellerActivity.module.scss'

interface ISellerActivityProps {
  activity: IAdminSellerActivity
}

const HOURS_IN_DAY = 24

const SHIPMENT_HINT =
  'Когда продавец последний раз передал заказ в доставку. Самовывоз не учитывается.'

const SHIPPING_TIME_HINT =
  'Среднее время от оплаты заказа покупателем (статус «На подтверждении») до статуса «В доставке» по последним 25 отправленным заказам продавца. Заказы с самовывозом не учитываются.'

const ACTIVITY_HINT =
  'Время последних действий продавца или его сотрудника в кабинете.'

const SELLER_CANCELS_HINT =
  'Отмены покупателем, администратором и автоматические не учитываются.'

const RESPONSE_TIME_HINT =
  'Среднее время, за которое селлер отвечает покупателям на сообщения. Короткие реплики покупателя вроде «Спасибо» или «Хорошо» не учитываются.'

const formatShippingTime = (hours: number) => {
  const rounded = Math.round(hours)
  if (rounded < 1) return 'меньше часа'

  const days = Math.floor(rounded / HOURS_IN_DAY)
  const restHours = rounded % HOURS_IN_DAY
  return [days && `${days}\u00a0д`, restHours && `${restHours}\u00a0ч`]
    .filter(Boolean)
    .join(' ')
}

const getBuyersWord = (count: number) =>
  count % 10 === 1 && count % 100 !== 11 ? 'покупателя' : 'покупателей'

const getResponseTimeText = ({
  average_minutes: averageMinutes,
  answered_buyers_count: answered,
  buyers_count: buyers
}: IAdminSellerResponseTime) => {
  const value =
    averageMinutes === null ? 'нет данных' : formatMinutes(averageMinutes)
  return buyers
    ? `${value} (ответил ${answered} из ${buyers} ${getBuyersWord(buyers)})`
    : value
}

const getCabinetVisitsText = ({ count, days }: IAdminSellerCabinetVisits) => {
  if (!count) return 'нет данных'

  const perDay = count / days
  if (perDay >= 1) {
    const times = Math.round(perDay)
    const timesWord = getDeclination(times % 100, ['раз', 'раз', 'раза'])
    return `${times} ${timesWord} в день`
  }

  const everyDays = Math.round(days / count)
  if (everyDays === 1) return '1 раз в день'
  const daysWord = getDeclination(everyDays % 100, ['дней', 'день', 'дня'])
  return `раз в ${everyDays} ${daysWord}`
}

const getCabinetVisitsHint = ({ count, days }: IAdminSellerCabinetVisits) => {
  const visits = getDeclination(count % 100, ['заходов', 'заход', 'захода'])
  const period = getDeclination(days % 100, ['дней', 'день', 'дня'])
  return `Как часто в среднем владелец или сотрудники заходят в кабинет за последний месяц: ${count} ${visits} за ${days} ${period}. Новый заход — после 30 минут без действий.`
}

const ORDER_COLORS = {
  completed: '#3fb950',
  canceled: '#f0525e',
  returned: '#fadb14'
}

const getOrderSegments = ({
  completed,
  canceled_by_seller: canceledBySeller,
  returned
}: IAdminSellerActivityOrders): ISegmentBarItem[] => [
  {
    key: 'completed',
    label: 'Выполнено',
    value: completed,
    color: ORDER_COLORS.completed
  },
  {
    key: 'canceled',
    label: 'Отменено продавцом',
    value: canceledBySeller,
    color: ORDER_COLORS.canceled
  },
  {
    key: 'returned',
    label: 'Отказы/Возвраты',
    value: returned,
    color: ORDER_COLORS.returned
  }
]

const Hint: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title}>
    <QuestionCircleOutlined className={styles.hint} />
  </Tooltip>
)

export const SellerActivity: React.FC<ISellerActivityProps> = ({
  activity
}) => {
  const {
    stock,
    last_shipment: lastShipment,
    shipping_time: shippingTime,
    response_time: responseTime,
    cabinet_visits: cabinetVisits,
    orders
  } = activity
  const stockTag = getStockConfirmationTag(stock)

  const getShipmentText = () => {
    if (!lastShipment.at) return '—'

    const order = lastShipment.order_number
      ? `заказ №${lastShipment.order_number}`
      : 'заказ'
    return lastShipment.is_exact
      ? `${formatStockConfirmationDate(lastShipment.at)} (${order})`
      : `${order} от ${dayjs(lastShipment.at).format('DD.MM.YYYY')}`
  }

  const getDisabledAtText = () => {
    if (!activity.is_disabled) return 'нет'

    return activity.disabled_at
      ? dayjs(activity.disabled_at).format('DD.MM.YYYY')
      : 'нет данных'
  }

  const shippingTimeValue =
    shippingTime.average_hours === null
      ? null
      : formatShippingTime(shippingTime.average_hours)
  const shippingTimeText = shippingTimeValue
    ? `${shippingTimeValue} (заказов: ${shippingTime.orders_count})`
    : 'нет данных'

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <Typography.Text className={styles.title}>
          Активность продавца
        </Typography.Text>
        <div className={styles.tags}>
          <SellerDisableStatus seller={activity} />
          <Tag color={stockTag.color}>{stockTag.label}</Tag>
        </div>
      </div>
      <div className={styles.rows}>
        <div className={styles.row}>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Обновление остатков:</span>{' '}
            {stock.lastConfirmedAt
              ? formatStockConfirmationDate(stock.lastConfirmedAt)
              : 'Ни разу'}
          </Typography.Text>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Способ:</span>{' '}
            {getStockConfirmationSourceText(stock)}
          </Typography.Text>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Без обновления:</span>{' '}
            {getStockDaysText(stock)}
            <StockTermsHint />
          </Typography.Text>
        </div>
        <div className={styles.row}>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Последняя активность:</span>{' '}
            {formatStockConfirmationDate(activity.last_activity_at)}
            <Hint title={ACTIVITY_HINT} />
          </Typography.Text>
          {cabinetVisits && (
            <Typography.Text className={styles.meta}>
              <span className={styles.label}>Частота посещения ЛК:</span>{' '}
              {getCabinetVisitsText(cabinetVisits)}
              <Hint title={getCabinetVisitsHint(cabinetVisits)} />
            </Typography.Text>
          )}
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Отключен:</span>{' '}
            {getDisabledAtText()}
          </Typography.Text>
        </div>
        <div className={styles.row}>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Последняя отправка:</span>{' '}
            {getShipmentText()}
            <Hint title={SHIPMENT_HINT} />
          </Typography.Text>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Средний срок отправки:</span>{' '}
            {shippingTimeText}
            <Hint title={SHIPPING_TIME_HINT} />
          </Typography.Text>
        </div>
        {responseTime && (
          <div className={styles.row}>
            <Typography.Text className={styles.meta}>
              <span className={styles.label}>
                Среднее время ответа покупателям:
              </span>{' '}
              {getResponseTimeText(responseTime)}
              <Hint title={RESPONSE_TIME_HINT} />
            </Typography.Text>
          </div>
        )}
        <div className={styles.row}>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Товары:</span> опубликовано{' '}
            {activity.published_products}
          </Typography.Text>
          <Typography.Text className={styles.meta}>
            <span className={styles.label}>Последний добавленный товар:</span>{' '}
            {formatStockConfirmationDate(activity.last_product_created_at)}
          </Typography.Text>
        </div>
        {orders && (
          <div className={styles.row}>
            <Typography.Text className={styles.meta}>
              <SegmentDot color={ORDER_COLORS.completed} />
              <span className={styles.label}>Выполнено заказов:</span>{' '}
              {orders.completed}
            </Typography.Text>
            <Typography.Text className={styles.meta}>
              <SegmentDot color={ORDER_COLORS.canceled} />
              <span className={styles.label}>Отмен продавцом:</span>{' '}
              {orders.canceled_by_seller}
              <Hint title={SELLER_CANCELS_HINT} />
            </Typography.Text>
            <Typography.Text className={styles.meta}>
              <SegmentDot color={ORDER_COLORS.returned} />
              <span className={styles.label}>Отказы/Возвраты:</span>{' '}
              {orders.returned}
            </Typography.Text>
            <SegmentBar
              className={styles.ordersBar}
              segments={getOrderSegments(orders)}
              total={
                orders.completed + orders.canceled_by_seller + orders.returned
              }
            />
          </div>
        )}
      </div>
    </section>
  )
}
